import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import _ from "lodash";
import APIs from "../../APIs";

import { useMatomo } from "@datapunt/matomo-tracker-react";

import { makeStyles } from "@material-ui/styles";
import {
    fade,
    Avatar,
    Button,
    Checkbox,
    Grid,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListSubheader,
    Typography,
} from "@material-ui/core";

import Preloader from "../../components/_common/Preloader";
import SearchRSISYSTEMY from "./SearchRSISYSTEMY";
import { useDocTitle } from "../../components/_common/DocTitle";
import { generatePDF } from "../../components/Report/functions";
import ReportTMPL from "../../components/Report/TMPL";

const AccessibilityTooltip = React.lazy(() => import("../../components/_common/AccessibilityTooltip"));

const useStyles = makeStyles((theme) => ({
    bigTitle: {
        margin: theme.spacing(0, 4, 4, 4),
        textAlign: "center",
        justifyContent: "center",
        [theme.breakpoints.down("sm")]: {
            fontSize: "1.125rem",
            fontWeight: theme.typography.fontWeightMedium,
        },
    },
    alphabet: {
        display: "flex",
        justifyContent: "space-between",
        overflow: "hidden",
    },
    listOverlay: {
        content: '""',
        position: "absolute",
        width: "100%",
        height: 1,
        bottom: -1,
        boxShadow: `0 1px 56px 64px ${theme.palette.background.paper}`,
        zIndex: 1,
    },
    listRoot: {
        maxHeight: "calc(100vh - 0px)",
        overflow: "hidden",
        overflowY: "scroll",
        marginTop: theme.spacing(4),
        paddingBottom: "100vh",
        position: "relative",
        scrollBehavior: "smooth",
    },
    listSection: {
        backgroundColor: "inherit",
    },
    ul: {
        backgroundColor: "inherit",
        padding: 0,
    },
    listSubheaderSticky: {
        backgroundColor: "rgb(234 237 244)",
        color: theme.palette.primary.contrastText,
        padding: theme.spacing(2),
    },
    listItemSelected: {
        backgroundColor: `${fade(theme.palette.primary.main, 0.25)}!important`,
    },
    alphabetChip: {
        backgroundColor: fade(theme.palette.primary.light, 0.5),
        color: theme.palette.primary.contrastText,
        "&:hover": {
            backgroundColor: theme.palette.primary.main,
        },
        "&:focus": {
            backgroundColor: theme.palette.primary.main,
        },
    },
    alphabetChipAcvtive: {
        backgroundColor: theme.palette.primary.main,
    },
    alphabetAvatar: {
        backgroundColor: theme.palette.primary.main,
    },
    listItemAvatarAvatar: {
        backgroundColor: fade(theme.palette.primary.main, 0.25),
    },
    formControl: {
        marginTop: theme.spacing(2),
        minWidth: "100%",
    },
    preRender: {
        position: "fixed",
        left: 0,
        right: "200vw",
        top: "200vh",
        width: "210mm",
    },

    pdfLoadingWrapper: {
        position: "fixed",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        backgroundColor: "rgba(255,255,255,0.75)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
    },
    listItemLI: {
        borderBottom: "1px solid rgba(0, 29, 119, 0.125)",
    },
    btnReport: {
        margin: "16px 8px",
        height: 40,
        minWidth: 150,
        [theme.breakpoints.down("sm")]: {
            margin: theme.spacing(4, 0, 4, 2),
        },
    },
}));

const RSISYSTEMY_Page = (props) => {
    const classes = useStyles();
    const { t } = useTranslation();
    useDocTitle(t("pages.systems"));

    const regex = /(<([^>]+)>)|\t|\n|&nbsp;/gi;

    const [isMobile, setIsMobile] = useState(window.innerWidth < 599.95);
    const [report, setReport] = useState(false);
    const [selected, setSelected] = useState("");
    const [searchText, setSearchText] = useState("");
    const [data, setData] = useState([]);
    const [sections, setSections] = useState([]);
    const [active, setActive] = useState("");
    const [gestors, setGestors] = useState([]);
    const [loadingPdf, setLoadingPDF] = useState(false);
    const [checked, setChecked] = useState([]);
    const [reportData, setReportData] = useState([]);

    useEffect(() => {
        window.addEventListener("resize", () => {
            setIsMobile(window.innerWidth < 599.96);
        });
    }, [isMobile]);

    useEffect(() => {
        data.length ||
            axios.get(APIs.dataURL.rsisystemy).then((result) => {
                const gestor_levels = {
                    "Administracja terenowa": {
                        name: "Administracja terenowa",
                        level: 3,
                    },
                    "Pozostała administracja centralna": {
                        name: "Pozostała administracja centralna",
                        level: 2,
                    },
                    Ministerstwo: {
                        name: "Ministerstwo",
                        level: 1,
                    },
                };

                const apiData = result.data.results
                    .map((item) => ({
                        ...item,
                        gestor_level: gestor_levels[item.gestor_typ]?.level,
                        nazwa_systemu: item.nazwa_systemu.replace(/(<([^>]+)>)|\t|\n/gi, ""),
                    }))
                    .sort((a, b) => -b.nazwa_systemu.toUpperCase().localeCompare(a.nazwa_systemu.toUpperCase()));

                setData(apiData);
                const s = apiData
                    .map((item) => item.nazwa_systemu.substring(0, 1).toUpperCase())
                    .filter((value, index, self) => self.indexOf(value) === index)
                    .sort((a, b) => -b.localeCompare(a));

                setSections(s);
                setActive(s[0]);
                // setFirstLetter(s[0]);

                setGestors(
                    _.uniqBy(
                        apiData.map((a) => ({
                            gestor_typ: a.gestor_typ,
                            gestor_level: a.gestor_level,
                            gestor: a.gestor,
                        })),
                        "gestor"
                    )
                );
            });
    }, [data.length]);

    const handleScroll = (event) => {
        try {
            sections.forEach((s) => {
                const anchor = ((event && event.target.ownerDocument) || document).querySelector(`#sub-${s}`);
                const aList = ((event && event.target.ownerDocument) || document).querySelector(`#aList`);
                if (anchor.getBoundingClientRect().top === aList.getBoundingClientRect().top && active !== s) {
                    // setTimeout(() => setActive(s), 123)
                }
            });
        } catch (e) {}
    };

    const handleSearchClick = ({ itemId, firstLetter }) => {
        const anchor = document.querySelector(`#item-${itemId}`);
        const aList = document.querySelector(`#aList`);

        if (anchor) {
            setSelected(itemId.toString());
            // setFirstLetter(firstLetter);
            let topPos = anchor.offsetTop;
            // console.log(itemId, anchor, topPos)
            aList.scrollTop = topPos - 60;
        }
    };

    const handleSearchInputChange = (args) => {
        // console.log('handleSearchInputChange args: ', args)
        setSearchText(args);
    };

    const handleToggle = (value) => () => {
        const currentIndex = checked.indexOf(value);
        const newChecked = [...checked];

        if (currentIndex === -1) {
            newChecked.push(value);
        } else {
            newChecked.splice(currentIndex, 1);
        }

        setChecked(newChecked);
    };

    const exportPdf = async (PDFOptions) => {
        // const selectedData = _.orderBy(data.filter(d => checked.includes(d.id_systemu)), ['gestor_level', 'nazwa_systemu'], ['asc', 'asc'])
        const selectedData = data
            .filter((d) => checked.includes(d.id_systemu))
            .sort((a, b) => a.gestor_level - b.gestor_level || a.nazwa_systemu.localeCompare(b.nazwa_systemu));

        let pdfData = [];

        setLoadingPDF(true);

        selectedData.forEach((item) => {
            axios
                .get(`${APIs.dataURL.rsisystemy}/${item.id_systemu}`)
                .then((result) => {
                    pdfData.push({
                        ...item,
                        ...result.data,
                    });
                })
                .catch((error) => {
                    console.log(error);
                })
                .finally(() => {
                    if (selectedData.length === pdfData.length) {
                        // setReportData(_.orderBy(pdfData, ['nazwa_systemu', 'gestor_level'], ['asc', 'asc']))
                        setReportData(
                            pdfData.sort(
                                (a, b) =>
                                    a.gestor_level - b.gestor_level || a.nazwa_systemu.localeCompare(b.nazwa_systemu)
                            )
                        );

                        setTimeout(() => {
                            // generatePDF({
                            //     data: _.orderBy(pdfData, ['gestor_level', 'nazwa_systemu'], ['asc', 'asc']),
                            //     gestors
                            // })
                            generatePDF({
                                data: pdfData.sort(
                                    (a, b) =>
                                        a.gestor_level - b.gestor_level ||
                                        a.nazwa_systemu.localeCompare(b.nazwa_systemu)
                                ),
                                gestors,
                            });
                            setLoadingPDF(false);
                        }, 1234);
                    }
                });
        });

        // generatePDF({
        //     data: pdfData,
        //     gestors
        // })

        return;

        // const doc = <DocPDF size="A4" {...PDFOptions} />;
        // const asPdf = pdf([]);
        // asPdf.updateContainer(doc);
        // const blob = await asPdf.toBlob();

        // saveAs(blob, `RSI_report_${reportDate()}.pdf`)
    };

    const handleGetReport = () => {
        report && setChecked([]);
        setReport(!report);
        if (!checked.length) return;
        // console.log('Get report: ', checked)
        exportPdf({
            abouts: data.filter((d) => checked.includes(d.id_systemu)),
        });
    };

    // Track page view
    const { trackPageView } = useMatomo();
    React.useEffect(() => {
        trackPageView();
        // eslint-disable-next-line
    }, []);

    return (
        <>
            <Typography variant="h4" className={classes.bigTitle} color="primary">
                {t("pages.systems")}
            </Typography>
            {!!loadingPdf && (
                <div className={classes.pdfLoadingWrapper}>
                    <img src={"/images/preloader833.gif"} style={{ width: 128 }} alt={"Loading..."} />
                </div>
            )}
            {!!reportData.length && (
                <div className={classes.preRender}>
                    <ReportTMPL reportData={reportData} />
                </div>
            )}
            <Grid container>
                <Grid item xs={12} style={{ display: "flex" }}>
                    {data.length ? (
                        <>
                            <SearchRSISYSTEMY
                                dataSet={data}
                                onSelect={(args) => handleSearchClick(args)}
                                onInputChange={(args) => handleSearchInputChange(args)}
                            />
                            <Button
                                variant={"contained"}
                                color={"primary"}
                                onClick={handleGetReport}
                                className={classes.btnReport}
                            >
                                {checked.length ? "Pobierz raport" : "Raport"}
                            </Button>
                        </>
                    ) : (
                        <Preloader type="linear" />
                    )}
                </Grid>
                <Grid item xs={12} style={{ position: "relative", overflow: "hidden" }} id="aGrid">
                    <div className={classes.listOverlay0} />
                    <List
                        className={classes.listRoot0}
                        dense={true}
                        subheader={<li />}
                        onWheel={handleScroll}
                        onTouchMove={handleScroll}
                        id="aList"
                    >
                        {data.length && sections.length ? (
                            sections.map((section, si) => {
                                if (
                                    data
                                        .filter((d) => d.nazwa_systemu.toUpperCase().startsWith(section))
                                        .filter((d) =>
                                            searchText
                                                ? d.nazwa_systemu.toUpperCase().includes(searchText.toUpperCase())
                                                : d
                                        ).length
                                )
                                    return (
                                        <li key={`section-${si}`} className={classes.listSection} id={`to-${section}`}>
                                            <ul className={classes.ul} id={`ul-${section}`}>
                                                <ListSubheader
                                                    className={classes.listSubheaderSticky}
                                                    id={`sub-${section}`}
                                                >
                                                    <Avatar classes={{ colorDefault: classes.alphabetAvatar }}>
                                                        {section}
                                                    </Avatar>
                                                </ListSubheader>
                                                {data
                                                    .filter((d) => d.nazwa_systemu.toUpperCase().startsWith(section))
                                                    .filter((d) =>
                                                        searchText
                                                            ? d.nazwa_systemu
                                                                  .toUpperCase()
                                                                  .includes(searchText.toUpperCase())
                                                            : d
                                                    )
                                                    .map((item, key) => {
                                                        return (
                                                            <ListItem
                                                                selected={selected === item.id_systemu.toString()}
                                                                dense={true}
                                                                className={classes.listItemLI}
                                                                classes={{ selected: classes.listItemSelected }}
                                                                key={key}
                                                                id={`item-${item.id_systemu}`}
                                                                onClick={handleToggle(item.id_systemu)}
                                                            >
                                                                {report && (
                                                                    <ListItemIcon>
                                                                        <AccessibilityTooltip
                                                                            arrow={false}
                                                                            placement="bottom"
                                                                            title={
                                                                                checked.indexOf(item.id_systemu) !== -1
                                                                                    ? `Usuń z rapotu`
                                                                                    : `Dodaj do raportu`
                                                                            }
                                                                        >
                                                                            <Checkbox
                                                                                edge="start"
                                                                                checked={
                                                                                    checked.indexOf(item.id_systemu) !==
                                                                                    -1
                                                                                }
                                                                                tabIndex={-1}
                                                                                disableRipple
                                                                                inputProps={{
                                                                                    "aria-labelledby": item.id_systemu,
                                                                                }}
                                                                                color={"primary"}
                                                                            />
                                                                        </AccessibilityTooltip>
                                                                    </ListItemIcon>
                                                                )}
                                                                <ListItemText
                                                                    primary={
                                                                        <a
                                                                            href={`/#/rsisystemy/${item.id_systemu}`}
                                                                            rel="noopener noreferrer"
                                                                            style={{
                                                                                color: "inherit",
                                                                                textDecoration: "inherit",
                                                                            }}
                                                                        >
                                                                            {item.nazwa_systemu}
                                                                        </a>
                                                                    }
                                                                    secondary={`${
                                                                        item.nazwa_systemu_skr
                                                                            ? item.nazwa_systemu_skr.replace(
                                                                                  regex,
                                                                                  ""
                                                                              ) + ", "
                                                                            : ""
                                                                    } ${
                                                                        item.gestor
                                                                            ? item.gestor.replace(regex, "")
                                                                            : ""
                                                                    }`}
                                                                    classes={{ primary: classes.listItemTextPrimary }}
                                                                />
                                                            </ListItem>
                                                        );
                                                    })}
                                            </ul>
                                        </li>
                                    );
                                return false;
                            })
                        ) : (
                            <>
                                <Preloader type="linear" />
                                <Preloader type="linear" />
                                <Preloader type="linear" />
                            </>
                        )}
                    </List>
                </Grid>
            </Grid>
        </>
    );
};

export default RSISYSTEMY_Page;
